import { call } from 'typed-redux-saga';

import { GRID_VIEW } from '../../../pages/call/constants';
import { updateConferenceConfig } from '../../../pages/call/features/conference/sagas/conference-config';
import { BroadcastStatus } from '../../../types';
import { cloudApi } from '../../helpers/transport';
import { CloudAPI } from '../../helpers/transport/cloud-api/types';
import { ConferenceConfigOptions } from './types';

interface CreatedBroadcastData extends CloudAPI.CreateBroadcast.Response {
    status: BroadcastStatus;
}

interface BroadcastManagerConfig {
    conferenceConfigOptions: ConferenceConfigOptions;
    uri: string;
    broadcastUri?: string;
}

export class BroadcastManager {
    private conferenceConfigOptions: Maybe<ConferenceConfigOptions>;
    private uri: Maybe<string>;
    private broadcastUri: Maybe<string>;

    public init(config: BroadcastManagerConfig) {
        this.uri = config.uri;
        this.broadcastUri = config.broadcastUri;
        this.conferenceConfigOptions = config.conferenceConfigOptions;
    }

    public get isReady(): boolean {
        return Boolean(this.uri);
    }

    public *createBroadcast(broadcastData: CloudAPI.CreateBroadcast.Request) {
        if (!this.uri) {
            return;
        }

        const result = yield* call([cloudApi, cloudApi.createBroadcast], this.uri, broadcastData);

        const resultBroadcastData: CreatedBroadcastData = {
            status: BroadcastStatus.CREATED,
            ...result,
        };

        this.broadcastUri = result.broadcast_uri;

        return resultBroadcastData;
    }

    public *startBroadcast(view: GRID_VIEW) {
        if (!this.uri) {
            return;
        }

        if (!this.broadcastUri) {
            throw new Error('No broadcast_uri');
        }

        const response = yield* call([cloudApi, cloudApi.startBroadcast], this.uri, this.broadcastUri, view);

        // TODO: Выпилить когда start начнёт отдавать stream_uri
        if (!response.stream_uri && this.conferenceConfigOptions) {
            const { roomId, searchString, displayName } = this.conferenceConfigOptions;
            const config = yield* call(updateConferenceConfig, roomId, searchString, displayName);
            response.stream_uri = config.conference_state?.broadcast_data?.stream_uri as string;
        }
        return response;
    }

    public *stopBroadcast() {
        if (!this.uri) {
            return;
        }

        if (!this.broadcastUri) {
            throw new Error('No broadcast_uri');
        }

        return yield* call([cloudApi, cloudApi.stopBroadcast], this.uri, this.broadcastUri);
    }

    public *endBroadcast() {
        if (!this.uri) {
            return;
        }

        if (!this.broadcastUri) {
            throw new Error('No broadcast_uri');
        }

        return yield* call([cloudApi, cloudApi.endBroadcast], this.uri, this.broadcastUri);
    }

    public *setViewInBroadcast(view: GRID_VIEW) {
        if (!this.uri) {
            return;
        }

        if (!this.broadcastUri) {
            throw new Error('No broadcast_uri');
        }

        yield* call([cloudApi, cloudApi.setViewInBroadcast], this.uri, this.broadcastUri, view);
    }

    public reinit(config: BroadcastManagerConfig) {
        this.destroy();
        this.init(config);
    }

    public destroy() {
        this.conferenceConfigOptions = undefined;
        this.uri = undefined;
        this.broadcastUri = undefined;
    }
}
